import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, WA_DEFAULT, PHONE_CALL_TEL } from "./data";
import { scrollToId } from "@/lib/lenis";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { MobileMenu } from "./MobileMenu";

const useScrolled = (threshold) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
};

const DesktopActions = () => (
  <div className="hidden items-center gap-3 lg:flex">
    <a
      href={`tel:${PHONE_CALL_TEL}`}
      data-testid="nav-call-cta"
      aria-label="Call Lift Edge"
      className="flex h-10 w-10 items-center justify-center border border-line text-ash transition-colors duration-200 hover:border-edge hover:text-white"
    >
      <Phone size={15} />
    </a>
    <a
      href={WA_DEFAULT}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="nav-whatsapp-cta"
      className="clip-corner-sm flex items-center gap-2 bg-edge px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-edgehot"
    >
      <WhatsAppIcon className="h-4 w-4" />
      WhatsApp Us
    </a>
  </div>
);

export default function Navbar() {
  const scrolled = useScrolled(24);
  const [open, setOpen] = useState(false);

  const go = (e, hash) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(hash);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          scrolled
            ? "border-b border-white/10 bg-black/85 backdrop-blur-xl"
            : "border-b border-transparent bg-gradient-to-b from-black/70 to-transparent"
        }`}
      >
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-5 lg:px-8">
          <a
            href="#home"
            data-testid="nav-logo-link"
            onClick={(e) => go(e, "#home")}
            aria-label="Lift Edge Hercules Fitness Store — home"
          >
            <Logo />
          </a>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <a
                key={l.hash}
                href={l.hash}
                data-testid={l.testid}
                onClick={(e) => go(e, l.hash)}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-ash transition-colors duration-200 hover:text-white"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <DesktopActions />

          <button
            className="flex h-10 w-10 items-center justify-center border border-line text-white lg:hidden"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </header>

      <MobileMenu open={open} onNavigate={go} />
    </>
  );
}
